import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { PhoneCall, X, Play, Pause, Gauge, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../hooks/useAuth";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
    fetchMyCampaigns,
    startMyCampaign,
    pauseMyCampaign,
    updateMyCampaignConcurrency
} from "../../services/crmServices/campaignService";

const STORAGE_POS_KEY = "crm_my_campaigns_pos";
// Lightweight self-service polling — just refreshing counters, not the admin dashboard.
const POLL_MS = 12000;

const STATUS_STYLES = {
    draft: "bg-gray-100 text-gray-700",
    running: "bg-green-100 text-green-700",
    paused: "bg-yellow-100 text-yellow-700",
    completed: "bg-blue-100 text-blue-700",
    cancelled: "bg-red-100 text-red-700"
};

function readPosition() {
    try {
        const raw = localStorage.getItem(STORAGE_POS_KEY);
        if (!raw) return null;
        const p = JSON.parse(raw);
        if (typeof p.x === "number" && typeof p.y === "number") return p;
        return null;
    } catch { return null; }
}

function writePosition(pos) {
    try { localStorage.setItem(STORAGE_POS_KEY, JSON.stringify(pos)); } catch { /* ignore */ }
}

// Floating, draggable panel (same interaction pattern as WorldClockWidget) that
// only appears for a logged-in user who is the assigned transfer/receiving
// agent on at least one campaign — lets them self-manage the pace of calls
// being routed to them without needing an admin.
export function MyCampaignsWidget() {
    const { user, isAuthenticated } = useAuth();
    const [campaigns, setCampaigns] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(() => readPosition());
    const [busyId, setBusyId] = useState(null);
    const [speedDrafts, setSpeedDrafts] = useState({});

    const buttonRef = useRef(null);
    const panelRef = useRef(null);
    const dragData = useRef({ dragging: false, offsetX: 0, offsetY: 0, moved: false });

    // Default position: below the world clock so the two don't overlap.
    const effectivePos = pos || (() => {
        if (typeof window === "undefined") return { x: 24, y: 144 };
        return { x: Math.max(24, window.innerWidth - 64), y: 144 };
    })();

    const load = useCallback(() => {
        if (!isAuthenticated || !user?._id) return;
        fetchMyCampaigns()
            .then((res) => {
                if (res.status === 200) setCampaigns(res.data || []);
            })
            .catch(() => { /* not the assigned agent for anything, or transient error — stay hidden */ })
            .finally(() => setLoaded(true));
    }, [isAuthenticated, user?._id]);

    useEffect(() => {
        load();
        const t = setInterval(load, POLL_MS);
        return () => clearInterval(t);
    }, [load]);

    // Seed the concurrency input per campaign without clobbering in-progress edits.
    useEffect(() => {
        setSpeedDrafts((prev) => {
            const next = { ...prev };
            let changed = false;
            campaigns.forEach((c) => {
                if (next[c._id] === undefined) {
                    next[c._id] = String(c.concurrencyLimit ?? 5);
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [campaigns]);

    // ── Drag handlers (identical pattern to WorldClockWidget) ────────────
    const handlePointerDown = useCallback((e) => {
        if (e.button !== undefined && e.button !== 0) return;
        const rect = buttonRef.current?.getBoundingClientRect();
        if (!rect) return;
        const clientX = e.clientX ?? e.touches?.[0]?.clientX;
        const clientY = e.clientY ?? e.touches?.[0]?.clientY;
        dragData.current = {
            dragging: true,
            offsetX: clientX - rect.left,
            offsetY: clientY - rect.top,
            moved: false
        };
        e.preventDefault();
    }, []);

    useEffect(() => {
        const handleMove = (e) => {
            if (!dragData.current.dragging) return;
            const clientX = e.clientX ?? e.touches?.[0]?.clientX;
            const clientY = e.clientY ?? e.touches?.[0]?.clientY;
            if (clientX === undefined || clientY === undefined) return;
            const newX = clientX - dragData.current.offsetX;
            const newY = clientY - dragData.current.offsetY;
            const maxX = window.innerWidth - 48;
            const maxY = window.innerHeight - 48;
            const x = Math.max(8, Math.min(maxX, newX));
            const y = Math.max(8, Math.min(maxY, newY));
            const dx = Math.abs(x - effectivePos.x);
            const dy = Math.abs(y - effectivePos.y);
            if (dx > 3 || dy > 3) dragData.current.moved = true;
            setPos({ x, y });
        };
        const handleUp = () => {
            if (dragData.current.dragging) {
                if (dragData.current.moved) {
                    writePosition(effectivePos);
                } else {
                    setOpen((v) => !v);
                }
            }
            dragData.current.dragging = false;
        };
        window.addEventListener("mousemove", handleMove);
        window.addEventListener("mouseup", handleUp);
        window.addEventListener("touchmove", handleMove);
        window.addEventListener("touchend", handleUp);
        return () => {
            window.removeEventListener("mousemove", handleMove);
            window.removeEventListener("mouseup", handleUp);
            window.removeEventListener("touchmove", handleMove);
            window.removeEventListener("touchend", handleUp);
        };
    }, [effectivePos]);

    useEffect(() => {
        if (!open) return;
        const onClick = (e) => {
            if (
                panelRef.current && !panelRef.current.contains(e.target) &&
                buttonRef.current && !buttonRef.current.contains(e.target)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    const panelPos = useMemo(() => {
        const panelWidth = 320;
        const panelHeight = Math.min(460, 80 + campaigns.length * 150);
        let left = effectivePos.x;
        let top = effectivePos.y + 56;
        if (left + panelWidth > window.innerWidth - 8) {
            left = Math.max(8, effectivePos.x + 48 - panelWidth);
        }
        if (top + panelHeight > window.innerHeight - 8) {
            top = Math.max(8, effectivePos.y - panelHeight - 8);
        }
        return { left, top };
    }, [effectivePos, campaigns.length]);

    const runningCount = campaigns.filter((c) => c.status === "running").length;

    const handleStartPause = async (c) => {
        setBusyId(c._id);
        try {
            if (c.status === "running") {
                await pauseMyCampaign(c._id);
                toast.success(`Paused "${c.name}"`);
            } else {
                await startMyCampaign(c._id);
                toast.success(c.status === "paused" ? `Resumed "${c.name}"` : `Started "${c.name}"`);
            }
            load();
        } catch (err) {
            toast.error(err?.response?.data?.message || "That action failed");
        } finally {
            setBusyId(null);
        }
    };

    const handleSaveSpeed = async (c) => {
        const val = parseInt(speedDrafts[c._id], 10);
        if (!val || val < 1 || val > 50) {
            toast.error("Call speed must be between 1 and 50");
            return;
        }
        setBusyId(c._id);
        try {
            await updateMyCampaignConcurrency(c._id, val);
            toast.success("Call speed updated");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to update call speed");
        } finally {
            setBusyId(null);
        }
    };

    // Nothing to show for this user — stay completely invisible.
    if (!isAuthenticated || !loaded || campaigns.length === 0) return null;

    return (
        <>
            {/* Floating draggable button */}
            <button
                ref={buttonRef}
                onMouseDown={handlePointerDown}
                onTouchStart={handlePointerDown}
                style={{
                    position: "fixed",
                    left: effectivePos.x,
                    top: effectivePos.y,
                    zIndex: 60,
                    touchAction: "none",
                    userSelect: "none",
                    cursor: dragData.current.dragging ? "grabbing" : "grab"
                }}
                className="relative h-12 w-12 rounded-full bg-gradient-to-br from-indigo-600 to-indigo-800 hover:from-indigo-500 hover:to-indigo-700 text-white shadow-lg flex items-center justify-center transition-shadow border-2 border-white"
                title="My Campaigns — drag to move, click to open"
                aria-label="My campaigns"
            >
                <PhoneCall className="h-5 w-5" />
                {runningCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-green-500 border-2 border-white text-[10px] font-bold text-white flex items-center justify-center">
                        {runningCount}
                    </span>
                )}
            </button>

            {/* Panel */}
            {open && (
                <div
                    ref={panelRef}
                    style={{
                        position: "fixed",
                        left: panelPos.left,
                        top: panelPos.top,
                        zIndex: 60,
                        width: 320
                    }}
                    className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col"
                >
                    {/* Header */}
                    <div className="px-3 py-2.5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
                        <div className="flex items-center gap-1.5">
                            <PhoneCall className="h-3.5 w-3.5 text-indigo-600" />
                            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">My Campaigns</span>
                        </div>
                        <button
                            onClick={() => setOpen(false)}
                            className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"
                            aria-label="Close"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>

                    {/* Campaign list */}
                    <div className="max-h-96 overflow-y-auto p-2 space-y-2">
                        {campaigns.map((c) => {
                            const totals = c.totals || {};
                            const total = (totals.queued || 0) + (totals.calling || 0) + (totals.completed || 0) + (totals.failed || 0);
                            const done = (totals.completed || 0) + (totals.failed || 0);
                            const progress = total > 0 ? Math.round((done / total) * 100) : 0;
                            const canStart = c.status === "draft" || c.status === "paused";
                            const canPause = c.status === "running";
                            const canEditSpeed = ["draft", "running", "paused"].includes(c.status);
                            const busy = busyId === c._id;

                            return (
                                <div key={c._id} className="rounded-lg border border-gray-100 p-2.5 bg-gray-50/60">
                                    <div className="flex items-center justify-between gap-2 mb-1.5">
                                        <p className="text-xs font-semibold text-gray-900 truncate">{c.name}</p>
                                        <Badge className={`text-[10px] px-1.5 py-0 ${STATUS_STYLES[c.status] || ""}`}>
                                            {c.status}
                                        </Badge>
                                    </div>

                                    <div className="flex items-center justify-between text-[11px] text-gray-500 mb-1">
                                        <span>{total} lead{total === 1 ? "" : "s"}</span>
                                        <span>{done} / {total} ({progress}%)</span>
                                    </div>
                                    <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mb-2">
                                        <div className="h-full bg-indigo-600 rounded-full transition-all" style={{ width: `${progress}%` }} />
                                    </div>

                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-1">
                                            {canStart && (
                                                <Button
                                                    size="sm"
                                                    disabled={busy}
                                                    onClick={() => handleStartPause(c)}
                                                    className="h-6 px-2 text-[11px] bg-green-600 hover:bg-green-700 text-white"
                                                >
                                                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3 mr-1" />}
                                                    {c.status === "paused" ? "Resume" : "Start"}
                                                </Button>
                                            )}
                                            {canPause && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    disabled={busy}
                                                    onClick={() => handleStartPause(c)}
                                                    className="h-6 px-2 text-[11px]"
                                                >
                                                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Pause className="h-3 w-3 mr-1" />}
                                                    Pause
                                                </Button>
                                            )}
                                        </div>

                                        {canEditSpeed && (
                                            <div className="flex items-center gap-1">
                                                <Gauge className="h-3 w-3 text-gray-400" title="Simultaneous calls" />
                                                <input
                                                    type="number"
                                                    min={1}
                                                    max={50}
                                                    value={speedDrafts[c._id] ?? ""}
                                                    onChange={(e) => setSpeedDrafts((prev) => ({ ...prev, [c._id]: e.target.value }))}
                                                    className="w-12 px-1 py-0.5 text-[11px] border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                                    title="Simultaneous calls (1–50)"
                                                />
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    disabled={busy || String(c.concurrencyLimit) === String(parseInt(speedDrafts[c._id], 10))}
                                                    onClick={() => handleSaveSpeed(c)}
                                                    className="h-6 px-1.5 text-[11px]"
                                                >
                                                    Save
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </>
    );
}
